import { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { ArrowRight, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Logo from './Logo';
import ordnr1Image from '../assets/ordnr1.jpg';

interface LoginPageProps {
  onSignup: () => void;
}

export default function LoginPage({ onSignup }: LoginPageProps) {
  const { login } = useAuth();
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError(t('login.fillAllFields'));
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
    } catch (error: any) {
      setError(error.message || t('login.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Logo */}
      <div className="auth-logo">
        <Logo className="w-14 h-14" />
      </div>
      
      <div className="auth-container">
        <div className="auth-layout">
          {/* Left Column - Form Content */}
          <div className="auth-form-column">
            {/* Header */}
            <div className="auth-header">
              <h1 className="auth-title">{t('login.title')}</h1>
            </div>

            {/* Main Content */}
            <div className="auth-content">
              <div className="auth-form-content">
                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <div className="auth-input-group">
                  <Label htmlFor="email" className="auth-label">{t('login.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    className="auth-input"
                  />
                </div>

                <div className="auth-input-group">
                  <Label htmlFor="password" className="auth-label">{t('login.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={t('login.passwordPlaceholder')}
                    className="auth-input"
                  />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="auth-nav">
              <Button
                variant="outline"
                onClick={onSignup}
                className="auth-button-outline"
              >
                <UserPlus className="icon-sm mr-2" />
                {t('login.createAccount')}
              </Button>

              <Button
                onClick={handleLogin}
                disabled={isLoading}
                className="auth-button-primary"
              >
                {isLoading ? (
                  <>
                    <div className="spinner-border-sm"></div>
                    {t('login.loggingIn')}
                  </>
                ) : (
                  <>
                    {t('login.login')}
                    <ArrowRight className="icon-sm ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="auth-visual-column">
            <div className="auth-visual-inner">
              <div className="auth-visual-content">
                <img 
                  src={ordnr1Image} 
                  alt="Login Visual" 
                  className="auth-visual-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}